import { IKey } from '@/common/constants/ikey'
import type { ActionContext, ActionDependencies } from '@/services/action/types'
import {
  buildTreeTableSearchCriteriaKey,
  resolveTreeTableFieldNameFromPageFields,
} from '@/common/utils/treeTableSearch'
import {
  branchSetsTreeTableNextSearchFlag,
  branchSetsTreeTableSearchFlag,
  findTabRoutedSearchBranch,
  isTabRoutedSearchAction,
} from '@/services/action/parseTabRoutedSearchOnClick'
import { resolveTabRouteValue, resolveTreeTableNameForTabRouteBranch } from '@/services/action/tabRoutedSearchAction'

/** Элемент реестра формы, достаточный для resolveTreeTableNameForSubmit. */
export type TreeTableSearchElementRef = {
  formId?: string | number
  fieldName?: string
  elementType?: string
  getType?: () => string
}

export type TreeTableSearchElementsAccessor = () => Record<string, TreeTableSearchElementRef>

/** Критерий поиска, отправленный последним doAction  */
const submittedCriteriaByFormId = new Map<string, string>()

/** Счётчик: apply только после явного submit */
const pendingApplyTicketByFormId = new Map<string, number>()
const lastAppliedTicketByFormId = new Map<string, number>()

export function setTreeTableSearchSubmittedCriteria(
  formId: string | number,
  criteriaKey: string,
): void {
  submittedCriteriaByFormId.set(String(formId), criteriaKey)
}

export function getTreeTableSearchSubmittedCriteria(
  formId: string | number | undefined,
): string | undefined {
  if (formId == null || formId === '') return undefined
  return submittedCriteriaByFormId.get(String(formId))
}

function bumpTreeTableSearchApplyTicket(formId: string | number): number {
  const key = String(formId)
  const next = (pendingApplyTicketByFormId.get(key) ?? 0) + 1
  pendingApplyTicketByFormId.set(key, next)
  return next
}

export function hasPendingTreeTableSearchApply(
  formId: string | number | undefined,
): boolean {
  if (formId == null || formId === '') return false
  const key = String(formId)
  const pending = pendingApplyTicketByFormId.get(key) ?? 0
  const applied = lastAppliedTicketByFormId.get(key) ?? 0
  return pending > applied
}

export function markTreeTableSearchApplyConsumed(formId: string | number): void {
  const key = String(formId)
  const pending = pendingApplyTicketByFormId.get(key) ?? 0
  lastAppliedTicketByFormId.set(key, pending)
}

export function resetTreeTableSearchApplyState(formId: string | number): void {
  const key = String(formId)
  pendingApplyTicketByFormId.delete(key)
  lastAppliedTicketByFormId.delete(key)
  submittedCriteriaByFormId.delete(key)
}

function resolveActionHotKeyText(actionData: unknown): string {
  if (!actionData || typeof actionData !== 'object') return ''
  const rawHotKey = (actionData as { hotKey?: unknown }).hotKey
  if (Array.isArray(rawHotKey)) {
    return (
      rawHotKey
        .map((entry) => String((entry as { _text?: unknown })?._text ?? entry ?? '').trim())
        .find(Boolean) || ''
    )
  }
  return String((rawHotKey as { _text?: unknown })?._text ?? rawHotKey ?? '').trim()
}

function normalizeHotKeyLabel(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ')
}

function actionScriptBody(context: ActionContext): string {
  if (typeof context.onClick === 'string' && context.onClick.trim()) {
    return context.onClick
  }
  const fromData = context.data?.onClick
  return String(fromData?._text ?? fromData ?? '')
}

function resolveTreeTableNameForSubmit(
  formId: string | number,
  pageFields: Record<string, unknown>,
  getElements?: TreeTableSearchElementsAccessor,
): string | null {
  const fromPageFields = resolveTreeTableFieldNameFromPageFields(pageFields)
  if (fromPageFields) return fromPageFields
  if (!getElements) return null
  const wanted = String(formId)
  for (const el of Object.values(getElements())) {
    if (el == null || String(el.formId ?? '') !== wanted) continue
    const type = el.getType?.() ?? el.elementType ?? ''
    if (type !== 'TREETABLE') continue
    const name = String(el.fieldName ?? '').trim()
    if (name) return name
  }
  return null
}

/** onClick выставляет флаги поиска TreeTable */
function actionSetsTreeTableSearchFlags(tableName: string, script: string): boolean {
  if (!script.trim()) return false
  return (
    script.includes(`${tableName}${IKey.TT_SEARCH}`) ||
    script.includes(`${tableName}${IKey.TT_NEXT_SEARCH_POSITION}`)
  )
}

function scriptHasDynamicTreeTableSearchFlag(script: string): boolean {
  return /getElement\s*\([^)]+\)\.getName\s*\(\s*\)\s*\+\s*["']__search["']/.test(script)
}

function shouldSubmitTabRoutedTreeTableSearch(
  context: ActionContext,
  pageFields: Record<string, unknown>,
  script: string,
  options?: {
    searchString?: unknown
    getElements?: TreeTableSearchElementsAccessor
    pageData?: Record<string, unknown> | null
    mergedFields?: Record<string, unknown>
    dependencies?: ActionDependencies
  },
): boolean {
  const branchDeps =
    options?.dependencies ??
    (options?.getElements
      ? ({ formProvider: { getElements: options.getElements } } as ActionDependencies)
      : undefined)
  const routeValue = resolveTabRouteValue(
    pageFields,
    context,
    options?.pageData,
    options?.mergedFields,
    branchDeps,
  )
  const branch = findTabRoutedSearchBranch(script, routeValue)
  if (!branch) return false

  const isFindNext =
    branchSetsTreeTableNextSearchFlag(branch) &&
    normalizeHotKeyLabel(resolveActionHotKeyText(context.data)) === 'f3'
  if (isFindNext) return branchSetsTreeTableSearchFlag(branch)

  if (!branchSetsTreeTableSearchFlag(branch)) return false
  return String(options?.searchString ?? '').trim() !== ''
}

/**
 * Нужно ли считать action отправкой поиска TreeTable 
 */
export function shouldSubmitTreeTableSearch(
  context: ActionContext,
  pageFields: Record<string, unknown>,
  options?: {
    searchString?: unknown
    getElements?: TreeTableSearchElementsAccessor
    pageData?: Record<string, unknown> | null
    mergedFields?: Record<string, unknown>
    dependencies?: ActionDependencies
  },
): boolean {
  if (context.formId == null || context.formId === '') return false

  const script = actionScriptBody(context)
  if (isTabRoutedSearchAction(script)) {
    return shouldSubmitTabRoutedTreeTableSearch(context, pageFields, script, options)
  }

  const tableName = resolveTreeTableNameForSubmit(
    context.formId,
    pageFields,
    options?.getElements,
  )
  if (!tableName) return false

  if (
    !actionSetsTreeTableSearchFlags(tableName, script) &&
    !scriptHasDynamicTreeTableSearchFlag(script)
  ) {
    return false
  }

  if (normalizeHotKeyLabel(resolveActionHotKeyText(context.data)) === 'f3') return true

  return String(options?.searchString ?? '').trim() !== ''
}

export function markTreeTableSearchActionSubmitted(
  formId: string | number,
  pageFields: Record<string, unknown>,
  liveOverrides?: Record<string, { type?: string; value?: unknown } | undefined>,
  getElements?: TreeTableSearchElementsAccessor,
  options?: {
    actionScript?: string
    routeValue?: string
    getElementsForRoute?: () => Record<string, { elementId?: string | number; fieldName?: string; name?: string }>
  },
): void {
  let tableName = resolveTreeTableNameForSubmit(formId, pageFields, getElements)
  if (!tableName && options?.actionScript && options?.routeValue) {
    tableName =
      resolveTreeTableNameForTabRouteBranch(options.actionScript, options.routeValue, {
        formProvider: {
          getElements: options.getElementsForRoute ?? getElements,
        },
      } as any) ?? null
  }
  if (!tableName) return
  bumpTreeTableSearchApplyTicket(formId)
  setTreeTableSearchSubmittedCriteria(
    formId,
    buildTreeTableSearchCriteriaKey(pageFields, tableName, { liveOverrides }),
  )
}
